import { DateTime } from 'luxon';
import { fromLocalJsDate } from '@/components/calendar/Calendar.utils';
import { isRestaurantClosed, DATESTR_FMT, TDR } from 'tdr-common';
import sortBy from 'lodash/sortBy';
import toPairs from 'lodash/toPairs';

/**
 * Find the first available date from calendar availability data
 * Returns the date string (YYYY-MM-DD) or undefined if no dates available
 */
export function findFirstAvailableDate(
	calendarAvailability: Record<string, TDR.Availability.dayAvailability> | Map<string, TDR.Availability.dayAvailability> | undefined
): string | undefined {
	if (!calendarAvailability) {
		return undefined;
	}

	// Handle both Map and Record types
	const entries = calendarAvailability instanceof Map
		? Array.from(calendarAvailability.entries())
		: Object.entries(calendarAvailability);

	return entries
		.sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
		.find(([, availability]) => availability.available > 0)?.[0];
}

/**
 * Find the first available timeslot from service timeslot data
 * Returns the time string (HH:MM) or undefined if no timeslots available
 *
 * Checks all services and returns the earliest available timeslot across any service
 */
export function findFirstAvailableTime(
	timeslotData: TDR.Availability.getServicesOutput | undefined
): string | undefined {
	if (!timeslotData?.services) {
		return undefined;
	}

	const services = timeslotData.services;
	const serviceKeys = Object.keys(services);

	if (serviceKeys.length === 0) {
		return undefined;
	}

	// Check all services in reverse order, preferring Dinner
	for (const serviceKey of [...serviceKeys].reverse()) {
		const service = services[serviceKey];
		if (!service?.timeSlots) {
			continue;
		}
		const timeslots: [string, TDR.TimeSlot][] = sortBy(
			toPairs(service.timeSlots),
			[([time]) => time]
		);
		if (timeslots.length > 0) {
			return timeslots[0][0];
		}
	}

	return undefined;
}

/**
 * Earliest available timeslot that falls within [windowStart, windowEnd] (inclusive).
 */
export function findFirstAvailableTimeInWindow(
	timeslotData: TDR.Availability.getServicesOutput | undefined,
	windowStart: string,
	windowEnd?: string
): string | undefined {
	if (!timeslotData?.services) {
		return undefined;
	}

	const effectiveEnd = windowEnd ?? windowStart;
	const inWindow: string[] = [];

	for (const service of Object.values(timeslotData.services)) {
		if (!service?.timeSlots) {
			continue;
		}
		for (const [time, slot] of Object.entries(service.timeSlots)) {
			if (slot?.available && time >= windowStart && time <= effectiveEnd) {
				inWindow.push(time);
			}
		}
	}

	return inWindow.sort((a, b) => a.localeCompare(b))[0];
}

export const isMonthAvailable = (restaurant: TDR.Restaurant, month: Date, monthAvailability: Map<string, TDR.Availability.dayAvailability>) => {
	if (!monthAvailability) {
		return null;
	}
	const TODAY = DateTime.now().setZone(restaurant?.timezone);
	const availableDates = monthAvailability;
	const monthStart = DateTime.fromJSDate(month).startOf('month');
	const monthEnd = DateTime.fromJSDate(month).endOf('month');
	let hasAvailableDate = false;

	for (
		let day = monthStart;
		day <= monthEnd;
		day = day.plus({ days: 1 })
	) {
		if (day < TODAY) {
			continue;
		}
		const dateStr = day.toFormat(DATESTR_FMT);
		const jsDate = day.toJSDate();
		const dayAvailability = availableDates[dateStr];

		if (dayAvailability) {
			const luxonDate = fromLocalJsDate(jsDate, restaurant?.timezone);
			if (
				dayAvailability.available > 0 &&
				!isRestaurantClosed(luxonDate, restaurant) &&
				dayAvailability.status !== 'closed'
			) {
				hasAvailableDate = true;
				break;
			}
		}
	}
	return hasAvailableDate;
};